import * as React from "react";
import { Camera, Paperclip, Plus, SendHorizontal } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "../primitives/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../primitives/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../primitives/tooltip";
import { Textarea } from "../primitives/textarea";

const DEFAULT_TEXTAREA_MAX_HEIGHT = 220;

export type PromptInputFile = {
  id: string;
  type: "file";
  mediaType: string;
  filename?: string;
  url: string;
};

export type PromptInputMessage = {
  text: string;
  files?: PromptInputFile[];
};

type PromptInputError = {
  code: "max_files" | "max_file_size" | "accept";
  message: string;
};

type PromptInputContextValue = {
  accept?: string;
  addFiles: (files: FileList | File[]) => Promise<void>;
  clearFiles: () => void;
  files: PromptInputFile[];
  maxFiles: number;
  maxFileSize: number;
  openFileDialog: () => void;
  removeFile: (id: string) => void;
};

const PromptInputContext = React.createContext<PromptInputContextValue | null>(null);

const usePromptInputContext = (): PromptInputContextValue => {
  const context = React.useContext(PromptInputContext);
  if (!context) throw new Error("PromptInput components must be used within PromptInput");
  return context;
};

const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const acceptsFile = (file: File, accept?: string): boolean => {
  if (!accept) return true;

  return accept.split(",").some((rawPattern) => {
    const pattern = rawPattern.trim();
    if (!pattern) return true;
    if (pattern.startsWith(".")) return file.name.toLowerCase().endsWith(pattern.toLowerCase());
    if (pattern.endsWith("/*")) return file.type.startsWith(pattern.slice(0, -1));
    return file.type === pattern;
  });
};

type PromptInputProps = Omit<React.FormHTMLAttributes<HTMLFormElement>, "onSubmit" | "onError"> & {
  accept?: string;
  globalDrop?: boolean;
  maxFiles?: number;
  maxFileSize?: number;
  multiple?: boolean;
  onError?: (error: PromptInputError) => void;
  onSubmit: (message: PromptInputMessage, event: React.FormEvent<HTMLFormElement>) => void;
};

const PromptInput = React.forwardRef<HTMLFormElement, PromptInputProps>(
  (
    {
      accept,
      children,
      className,
      globalDrop,
      maxFiles = 5,
      maxFileSize = 10 * 1024 * 1024,
      multiple,
      onError,
      onSubmit,
      ...props
    },
    ref,
  ) => {
    const fileInputRef = React.useRef<HTMLInputElement | null>(null);
    const [files, setFiles] = React.useState<PromptInputFile[]>([]);

    const reportError = React.useCallback(
      (error: PromptInputError) => {
        onError?.(error);
      },
      [onError],
    );

    const addFiles = React.useCallback(
      async (incomingFiles: FileList | File[]) => {
        const list = Array.from(incomingFiles);
        if (!list.length) return;

        if (files.length + list.length > maxFiles) {
          reportError({ code: "max_files", message: `Możesz dodać maksymalnie ${maxFiles} plików.` });
          return;
        }

        const invalidType = list.find((file) => !acceptsFile(file, accept));
        if (invalidType) {
          reportError({ code: "accept", message: `Nieobsługiwany typ pliku: ${invalidType.name}` });
          return;
        }

        const tooLarge = list.find((file) => file.size > maxFileSize);
        if (tooLarge) {
          reportError({ code: "max_file_size", message: `Plik jest za duży: ${tooLarge.name}` });
          return;
        }

        const nextFiles = await Promise.all(
          list.map(async (file) => ({
            id: crypto.randomUUID(),
            type: "file" as const,
            mediaType: file.type || "application/octet-stream",
            filename: file.name,
            url: await fileToDataUrl(file),
          })),
        );

        setFiles((currentFiles) => [...currentFiles, ...nextFiles]);
      },
      [accept, files.length, maxFileSize, maxFiles, reportError],
    );

    const clearFiles = React.useCallback(() => setFiles([]), []);
    const removeFile = React.useCallback(
      (id: string) => setFiles((currentFiles) => currentFiles.filter((file) => file.id !== id)),
      [],
    );
    const openFileDialog = React.useCallback(() => fileInputRef.current?.click(), []);

    React.useEffect(() => {
      if (!globalDrop) return;

      const handleDragOver = (event: DragEvent) => {
        if (!event.dataTransfer?.files.length) return;
        event.preventDefault();
      };
      const handleDrop = (event: DragEvent) => {
        if (!event.dataTransfer?.files.length) return;
        event.preventDefault();
        void addFiles(event.dataTransfer.files);
      };

      document.addEventListener("dragover", handleDragOver);
      document.addEventListener("drop", handleDrop);
      return () => {
        document.removeEventListener("dragover", handleDragOver);
        document.removeEventListener("drop", handleDrop);
      };
    }, [addFiles, globalDrop]);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const formData = new FormData(event.currentTarget);
      const text = String(formData.get("message") ?? "");
      onSubmit({ files, text }, event);
      clearFiles();
    };

    const contextValue = React.useMemo(
      () => ({ accept, addFiles, clearFiles, files, maxFiles, maxFileSize, openFileDialog, removeFile }),
      [accept, addFiles, clearFiles, files, maxFileSize, maxFiles, openFileDialog, removeFile],
    );

    return (
      <PromptInputContext.Provider value={contextValue}>
        <form
          ref={ref}
          className={cn("rounded-lg border border-gray-200 bg-white shadow-sm", className)}
          onSubmit={handleSubmit}
          {...props}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            multiple={multiple}
            className="hidden"
            onChange={(event) => {
              if (event.target.files) {
                void addFiles(event.target.files);
              }
              event.target.value = "";
            }}
          />
          {children}
        </form>
      </PromptInputContext.Provider>
    );
  },
);
PromptInput.displayName = "PromptInput";

const PromptInputHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("px-5 pb-2 pt-4", className)} {...props} />,
);
PromptInputHeader.displayName = "PromptInputHeader";

const PromptInputBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("px-5 pt-4", className)} {...props} />,
);
PromptInputBody.displayName = "PromptInputBody";

const PromptInputFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center justify-between gap-3 px-4 pb-3 pt-2", className)} {...props} />
  ),
);
PromptInputFooter.displayName = "PromptInputFooter";

const PromptInputTools = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("flex items-center gap-2", className)} {...props} />,
);
PromptInputTools.displayName = "PromptInputTools";

type PromptInputTextareaProps = React.ComponentPropsWithoutRef<typeof Textarea> & {
  autoResize?: boolean;
  maxHeight?: number;
};

const PromptInputTextarea = React.forwardRef<HTMLTextAreaElement, PromptInputTextareaProps>(
  (
    {
      autoResize = true,
      className,
      maxHeight = DEFAULT_TEXTAREA_MAX_HEIGHT,
      name = "message",
      onChange,
      onPaste,
      value,
      ...props
    },
    ref,
  ) => {
    const { addFiles } = usePromptInputContext();
    const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

    const setRefs = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        textareaRef.current = node;

        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );

    const resize = React.useCallback(() => {
      const textarea = textareaRef.current;
      if (!textarea || !autoResize) return;

      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
      textarea.style.overflowY = textarea.scrollHeight > maxHeight ? "auto" : "hidden";
    }, [autoResize, maxHeight]);

    React.useEffect(() => {
      resize();
    }, [resize, value]);

    return (
      <Textarea
        ref={setRefs}
        name={name}
        value={value}
        className={cn(
          "min-h-10 resize-none border-0 px-0 py-0 text-base shadow-none placeholder:text-gray-400 focus-visible:ring-0 focus-visible:ring-offset-0",
          className,
        )}
        onChange={(event) => {
          onChange?.(event);
          window.requestAnimationFrame(resize);
        }}
        onPaste={(event) => {
          onPaste?.(event);

          const pastedFiles = Array.from(event.clipboardData.files);
          if (!pastedFiles.length) return;

          event.preventDefault();
          void addFiles(pastedFiles);
        }}
        {...props}
      />
    );
  },
);
PromptInputTextarea.displayName = "PromptInputTextarea";

type PromptInputButtonProps = React.ComponentProps<typeof Button> & {
  tooltip?: string | { content: React.ReactNode; shortcut?: string; side?: "top" | "right" | "bottom" | "left" };
};

const PromptInputButton = React.forwardRef<HTMLButtonElement, PromptInputButtonProps>(
  ({ children, tooltip, ...props }, ref) => {
    const button = (
      <Button ref={ref} type="button" variant="ghost" size="sm" {...props}>
        {children}
      </Button>
    );

    if (!tooltip) return button;

    const content = typeof tooltip === "string" ? tooltip : tooltip.content;
    const side = typeof tooltip === "string" ? "top" : (tooltip.side ?? "top");
    const shortcut = typeof tooltip === "string" ? null : tooltip.shortcut;

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent side={side}>
            <span>{content}</span>
            {shortcut ? <span className="ml-2 text-xs opacity-60">{shortcut}</span> : null}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  },
);
PromptInputButton.displayName = "PromptInputButton";

type PromptInputSubmitProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  status?: "ready" | "submitted" | "streaming" | "error";
};

const PromptInputSubmit = React.forwardRef<HTMLButtonElement, PromptInputSubmitProps>(
  ({ children, status = "ready", disabled, className, ...props }, ref) => (
    <button
      ref={ref}
      type="submit"
      disabled={disabled || status === "streaming" || status === "submitted"}
      className={cn(
        "inline-flex h-10 w-10 items-center justify-center gap-2 rounded-full bg-gray-950 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:pointer-events-none disabled:opacity-50",
        className,
      )}
      {...props}
    >
      {children ?? (
        <>
          <SendHorizontal className="h-4 w-4" />
          Wyślij
        </>
      )}
    </button>
  ),
);
PromptInputSubmit.displayName = "PromptInputSubmit";

const PromptInputActionMenu = DropdownMenu;
const PromptInputActionMenuTrigger = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>(
  ({ children, ...props }, ref) => (
    <DropdownMenuTrigger asChild>
      <Button ref={ref} type="button" variant="ghost" size="icon" {...props}>
        {children ?? <Plus className="h-4 w-4" />}
      </Button>
    </DropdownMenuTrigger>
  ),
);
PromptInputActionMenuTrigger.displayName = "PromptInputActionMenuTrigger";

const PromptInputActionMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuContent>
>(({ className, ...props }, ref) => (
  <DropdownMenuContent
    ref={ref}
    className={cn("z-[1000] border-gray-200 bg-white text-gray-950 shadow-lg backdrop-blur-none", className)}
    {...props}
  />
));
PromptInputActionMenuContent.displayName = "PromptInputActionMenuContent";
const PromptInputActionMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuItem>
>(({ className, ...props }, ref) => (
  <DropdownMenuItem
    ref={ref}
    className={cn(
      "cursor-pointer focus:bg-gray-100 focus:text-gray-950 data-[highlighted]:bg-gray-100 data-[highlighted]:text-gray-950",
      className,
    )}
    {...props}
  />
));
PromptInputActionMenuItem.displayName = "PromptInputActionMenuItem";

const PromptInputActionAddAttachments = React.forwardRef<
  React.ElementRef<typeof DropdownMenuItem>,
  React.ComponentProps<typeof DropdownMenuItem> & { label?: string }
>(({ children, label = "Dodaj załącznik", onSelect, ...props }, ref) => {
  const { openFileDialog } = usePromptInputContext();

  return (
    <PromptInputActionMenuItem
      ref={ref}
      onSelect={(event) => {
        onSelect?.(event);
        openFileDialog();
      }}
      {...props}
    >
      {children ?? (
        <>
          <Paperclip className="mr-2 h-4 w-4" />
          {label}
        </>
      )}
    </PromptInputActionMenuItem>
  );
});
PromptInputActionAddAttachments.displayName = "PromptInputActionAddAttachments";

const PromptInputActionAddScreenshot = React.forwardRef<
  React.ElementRef<typeof DropdownMenuItem>,
  React.ComponentProps<typeof DropdownMenuItem> & { label?: string }
>(({ children, label = "Dodaj screenshot", ...props }, ref) => (
  <PromptInputActionMenuItem ref={ref} disabled {...props}>
    {children ?? (
      <>
        <Camera className="mr-2 h-4 w-4" />
        {label}
      </>
    )}
  </PromptInputActionMenuItem>
));
PromptInputActionAddScreenshot.displayName = "PromptInputActionAddScreenshot";

const usePromptInputAttachments = () => {
  const { addFiles, clearFiles, files, openFileDialog, removeFile } = usePromptInputContext();

  return React.useMemo(
    () => ({
      add: addFiles,
      clear: clearFiles,
      files,
      openFileDialog,
      remove: removeFile,
    }),
    [addFiles, clearFiles, files, openFileDialog, removeFile],
  );
};

export {
  PromptInput,
  PromptInputActionAddAttachments,
  PromptInputActionAddScreenshot,
  PromptInputActionMenu,
  PromptInputActionMenuContent,
  PromptInputActionMenuItem,
  PromptInputActionMenuTrigger,
  PromptInputBody,
  PromptInputButton,
  PromptInputFooter,
  PromptInputHeader,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputTools,
  usePromptInputAttachments,
};
